import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';
import { Route, UserSettings } from '../types';
import { apiClient } from '../api/client';
import { wsManager } from '../api/websocket';

/**
 * Глобальный стор — ТОЛЬКО то, что реально читают компоненты: auth, user,
 * routes. Легаси v1-слой (spreads/executions/whyNotEnter/systemHealth,
 * getExchangeData/getFundingComparison, route-операции, WS-каналы
 * spread_update/execution_update/health_update — ~60% старого файла) удалён:
 * бэкенд v2 таких каналов не шлёт, страницы ходят в apiClient напрямую.
 */
interface AppState {
  isAuthenticated: boolean;
  user: UserSettings | null;
  routes: Route[];
  wsConnected: boolean;

  setAuthenticated: (authenticated: boolean) => void;
  setUser: (user: UserSettings | null) => void;
  setRoutes: (routes: Route[]) => void;
  setWsConnected: (connected: boolean) => void;

  loadRoutes: () => Promise<void>;
  loadUserSettings: () => Promise<void>;
}

export const useAppStore = create<AppState>()(
  subscribeWithSelector((set) => ({
    isAuthenticated: apiClient.isAuthenticated(),
    user: null,
    routes: [],
    wsConnected: false,

    setAuthenticated: (authenticated) => set({ isAuthenticated: authenticated }),
    setUser: (user) => set({ user }),
    setRoutes: (routes) => set({ routes }),
    setWsConnected: (connected) => set({ wsConnected: connected }),

    loadRoutes: async () => {
      try {
        const routes = await apiClient.getRoutes();
        // Пустой список — нормальное состояние (маршрутов нет), НЕ повод
        // подставлять мок: фейковый маршрут попадал в счётчики табов и
        // OrderPanel как настоящий.
        set({ routes: Array.isArray(routes) ? routes : [] });
      } catch (error) {
        console.warn('routes API unavailable:', error);
        set({ routes: [] });
      }
    },

    loadUserSettings: async () => {
      try {
        const user = await apiClient.getSettings();
        set({ user });
      } catch (error) {
        console.error('Failed to load user settings:', error);
        // Профиль грузится один раз при открытии страницы. Попали на рестарт
        // бэкенда — кнопка профиля в шапке пропадала до ручной перезагрузки.
        // Повторяем, пока не получится (и пока человек залогинен).
        setTimeout(() => {
          const st = useAppStore.getState();
          if (st.isAuthenticated && !st.user) void st.loadUserSettings();
        }, 5000);
      }
    },
  }))
);

// Initialize WebSocket subscriptions and auth state
if (typeof window !== 'undefined') {
  wsManager.onConnectionChange((connected) => {
    const st = useAppStore.getState();
    st.setWsConnected(connected);
    // Сокет поднялся после рестарта сервера, а профиля нет — дозагружаем.
    if (connected && st.isAuthenticated && !st.user) void st.loadUserSettings();
  });
  if (useAppStore.getState().isAuthenticated) {
    wsManager.connect();
  }
}
